import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const SIGNALS = [
  { id: "01", name: "PHYSICAL", status: "CONNECTED" },
  { id: "02", name: "MIND", status: "UNRESOLVED" },
  { id: "03", name: "SPIRIT", status: "UNRESOLVED" },
  { id: "04", name: "BEYOND", status: "UNRESOLVED" },
];

export default function Intro({ onComplete }) {
  const [step, setStep] = useState(0);
  // steps: 0 point of light, 1..4 signals, 5 redacted glitch, 6 reveal, 7 exit

  useEffect(() => {
    const short = !!localStorage.getItem("mm_visited");
    localStorage.setItem("mm_visited", "1");

    const seq = short
      ? [[6, 200], [7, 1100]]
      : [
          [1, 600], [2, 900], [3, 1200], [4, 1500],
          [5, 2050], [6, 2500], [7, 4200],
        ];

    const timers = seq.map(([s, t]) => setTimeout(() => setStep(s), t));
    const done = setTimeout(() => onComplete(), (short ? 1300 : 4400));
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(done);
    };
  }, [onComplete]);

  return (
    <motion.div
      className="fixed inset-0 z-[10000] bg-[#040404] flex items-center justify-center overflow-hidden"
      exit={{ opacity: 0 }}
      transition={{ duration: 0.9, ease: "easeInOut" }}
      data-testid="intro-calibration"
    >
      <div className="mm-vignette" />

      {/* Point of light */}
      <motion.div
        className="absolute rounded-full"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.9), transparent 70%)" }}
        initial={{ width: 2, height: 2, opacity: 0 }}
        animate={
          step >= 6
            ? { width: 900, height: 900, opacity: 0.15 }
            : { width: 220, height: 220, opacity: 0.35 }
        }
        transition={{ duration: 1.6, ease: "easeInOut" }}
      />

      <div className="relative z-10 w-full max-w-md px-8">
        <AnimatePresence mode="wait">
          {step >= 1 && step <= 5 && (
            <motion.div
              key="signals"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="font-mono text-[11px] md:text-xs tracking-[0.15em]"
            >
              <p className="text-gold mb-6 tracking-[0.3em]">DIMENSIONAL SIGNAL DETECTED</p>
              <div className="space-y-3">
                {SIGNALS.map((s, i) => (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: step > i ? 1 : 0.15, x: 0 }}
                    className="flex items-center justify-between text-white/80"
                  >
                    <span>{s.id} — {s.name}</span>
                    <span className={s.status === "CONNECTED" ? "text-gold" : "text-white/35"}>
                      {"·".repeat(6)} {step > i ? s.status : "······"}
                    </span>
                  </motion.div>
                ))}
                {step >= 5 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0.2, 1, 0] }}
                    transition={{ duration: 0.5, times: [0, 0.2, 0.4, 0.6, 1] }}
                    className="flex items-center justify-between text-red-600"
                  >
                    <span>05 — [SIGNAL LOST]</span>
                    <span>▓▓▓▓</span>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}

          {step >= 6 && (
            <motion.div
              key="reveal"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="text-center"
            >
              <h1 className="font-serif text-[clamp(1.6rem,8.2vw,2.25rem)] md:text-6xl tracking-[0.1em] leading-none">
                MOTION <span className="text-gold">&</span> MEANING
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 1 }}
                className="font-mono text-[11px] tracking-[0.4em] text-white/60 mt-6"
              >
                MULTIDIMENSIONAL
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
